// Shared Markdown rendering for participant-facing surfaces.
//
// `marked` parses the source; `DOMPurify` sanitizes the resulting
// HTML before any consumer sets it via `dangerouslySetInnerHTML`.
// We never trust the markdown source — researchers may paste
// arbitrary content and we render it inside the same browser tab as
// session data.
//
// This lives in the `ui` package (rather than the Designer) so the
// participant kiosk — which renders SurveyForm but does not depend on
// the Designer — can render survey descriptions and other authored
// markdown safely. The Designer re-exports `renderMarkdown` from here
// so there is a single sanitization policy across the suite.

import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { cn } from './cn';

// Configure marked synchronously so renderMarkdown returns a string
// rather than a Promise. GFM is on by default in marked 14; this
// switch makes the option explicit and disables async tokenizers.
marked.use({ async: false, gfm: true, breaks: true });

export function renderMarkdown(source: string): string {
  if (!source) return '';
  const raw = marked.parse(source) as string;
  return DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });
}

export interface MarkdownProps {
  /** Raw markdown source authored in the Designer. */
  source: string;
  /** Extra classes merged onto the prose container. */
  className?: string;
}

/**
 * Render authored markdown as sanitized, prose-styled HTML. Shared by
 * every surface that shows researcher-authored copy to participants
 * (survey descriptions in the kiosk, the live preview, the run
 * preview) so the rendering and sanitization policy stays identical
 * across the suite. Returns null for empty source so callers can
 * mount it unconditionally.
 */
export function Markdown({ source, className }: MarkdownProps) {
  if (!source) return null;
  return (
    <div
      className={cn('prose prose-sm max-w-none dark:prose-invert', className)}
      dangerouslySetInnerHTML={{ __html: renderMarkdown(source) }}
    />
  );
}
